/**
 * Purchase Orders Service
 *
 * API calls for Purchase Orders.
 */

import { apiClient } from '@/api/client';
import type {
  PurchaseOrderListItem,
  PurchaseOrderWithDetails,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  AddOrderItemDto,
  UpdateOrderItemDto,
  QueryPurchaseOrdersDto,
  ApiSuccess,
  PurchaseOrdersListResponse,
  PurchaseOrderDetailResponse,
  PurchaseOrderCreateResponse,
  PurchaseOrderUpdateResponse,
  AddOrderItemResponse,
  DuplicateOrderResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  PurchaseOrderListItem,
  PurchaseOrderWithDetails,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  AddOrderItemDto,
  UpdateOrderItemDto,
  QueryPurchaseOrdersDto,
  DuplicateOrderResponse,
};

export const purchaseOrdersService = {
  /**
   * List purchase orders with pagination and filters
   */
  async list(params: QueryPurchaseOrdersDto): Promise<PurchaseOrdersListResponse> {
    const response = await apiClient.get<ApiSuccess<PurchaseOrdersListResponse>>(
      '/purchase-orders',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get single purchase order by ID
   */
  async getById(id: string): Promise<PurchaseOrderDetailResponse> {
    const response = await apiClient.get<ApiSuccess<PurchaseOrderDetailResponse>>(
      `/purchase-orders/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new purchase order
   */
  async create(data: CreatePurchaseOrderDto): Promise<PurchaseOrderCreateResponse> {
    const response = await apiClient.post<ApiSuccess<PurchaseOrderCreateResponse>>(
      '/purchase-orders',
      data
    );
    return response.data.data;
  },

  /**
   * Update a purchase order
   */
  async update(id: string, data: UpdatePurchaseOrderDto): Promise<PurchaseOrderUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<PurchaseOrderUpdateResponse>>(
      `/purchase-orders/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a purchase order (DRAFT only)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/purchase-orders/${id}`);
  },

  /**
   * Add an item to a purchase order
   */
  async addItem(orderId: string, data: AddOrderItemDto): Promise<AddOrderItemResponse> {
    const response = await apiClient.post<ApiSuccess<AddOrderItemResponse>>(
      `/purchase-orders/${orderId}/items`,
      data
    );
    return response.data.data;
  },

  /**
   * Update an item in a purchase order
   */
  async updateItem(
    orderId: string,
    itemId: string,
    data: UpdateOrderItemDto
  ): Promise<PurchaseOrderUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<PurchaseOrderUpdateResponse>>(
      `/purchase-orders/${orderId}/items/${itemId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Remove an item from a purchase order
   */
  async removeItem(orderId: string, itemId: string): Promise<PurchaseOrderUpdateResponse> {
    const response = await apiClient.delete<ApiSuccess<PurchaseOrderUpdateResponse>>(
      `/purchase-orders/${orderId}/items/${itemId}`
    );
    return response.data.data;
  },

  /**
   * Mark order as sent
   */
  async send(id: string): Promise<PurchaseOrderUpdateResponse> {
    const response = await apiClient.post<ApiSuccess<PurchaseOrderUpdateResponse>>(
      `/purchase-orders/${id}/send`
    );
    return response.data.data;
  },

  /**
   * Cancel an order
   */
  async cancel(id: string): Promise<PurchaseOrderUpdateResponse> {
    const response = await apiClient.post<ApiSuccess<PurchaseOrderUpdateResponse>>(
      `/purchase-orders/${id}/cancel`
    );
    return response.data.data;
  },

  /**
   * Duplicate an order
   */
  async duplicate(id: string): Promise<DuplicateOrderResponse> {
    const response = await apiClient.post<ApiSuccess<DuplicateOrderResponse>>(
      `/purchase-orders/${id}/duplicate`
    );
    return response.data.data;
  },
};
